import { BRAND_COLORS } from './brand';

export const SPACING_SCALE = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

export const RADIUS_SCALE = {
  sm: '4px',
  DEFAULT: '8px',
  lg: '12px',
  xl: '12px',
  '2xl': '12px',
  '3xl': '16px',
  full: '9999px',
} as const;

export const SHADOW_SCALE = {
  sm: '0 1px 2px rgba(15, 23, 42, 0.05)',
  default: '0 4px 6px rgba(15, 23, 42, 0.07)',
  md: '0 6px 12px rgba(15, 23, 42, 0.1)',
  card: '0 12px 30px rgba(15, 23, 42, 0.06)',
  'card-hover': '0 20px 40px rgba(15, 23, 42, 0.1)',
  xl: '0 20px 40px rgba(15, 23, 42, 0.12)',
} as const;

export const MOTION_TOKENS = {
  easing: {
    productive: 'ease-out',
  },
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    deliberate: '500ms',
  },
  transitionProperty: {
    interactive: 'background-color, border-color, color, box-shadow, transform, opacity',
  },
} as const;

export const SHELL_TOKENS = {
  page: {
    background: BRAND_COLORS.slate50,
    backgroundSoft: BRAND_COLORS.fog,
    sidebar: BRAND_COLORS.inkDark,
    topbar: BRAND_COLORS.white,
  },
  surface: {
    base: BRAND_COLORS.white,
    soft: BRAND_COLORS.mist,
    inverse: BRAND_COLORS.inkDark,
  },
  text: {
    heading: BRAND_COLORS.ink,
    body: BRAND_COLORS.slate700,
    muted: BRAND_COLORS.slate500,
    inverse: BRAND_COLORS.white,
  },
  border: {
    subtle: '#E2E8F0',
    default: BRAND_COLORS.slate300,
  },
  focus: {
    ring: BRAND_COLORS.green,
    ringAccessible: BRAND_COLORS.greenAccessible,
    ringShadow: 'rgba(150, 169, 152, 0.2)',
  },
} as const;

export const TYPOGRAPHY_TOKENS = {
  display: {
    fontSize: '2.75rem',
    lineHeight: '1.08',
    letterSpacing: '0',
    fontFamily: 'serif',
    fontWeight: '700',
  },
  pageTitle: {
    fontSize: '1.875rem',
    lineHeight: '1.15',
    letterSpacing: '0',
    fontFamily: 'serif',
    fontWeight: '700',
  },
  sectionTitle: {
    fontSize: '1.375rem',
    lineHeight: '1.25',
    letterSpacing: '0',
    fontFamily: 'serif',
    fontWeight: '700',
  },
  cardTitle: {
    fontSize: '1.0625rem',
    lineHeight: '1.35',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '600',
  },
  bodyLarge: {
    fontSize: '1.125rem',
    lineHeight: '1.6',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '400',
  },
  body: {
    fontSize: '1rem',
    lineHeight: '1.5',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '400',
  },
  bodySmall: {
    fontSize: '0.875rem',
    lineHeight: '1.5',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '400',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: '1.4',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '500',
  },
  eyebrow: {
    fontSize: '0.6875rem',
    lineHeight: '1.2',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '600',
  },
  metric: {
    fontSize: '1.75rem',
    lineHeight: '1.1',
    letterSpacing: '0',
    fontFamily: 'sans',
    fontWeight: '700',
  },
} as const;

export const CHART_TOKENS = {
  categorical: {
    primary: BRAND_COLORS.greenAccessible,
    secondary: BRAND_COLORS.ink,
    tertiary: BRAND_COLORS.deep,
    quaternary: '#4F6D5A',
    quinary: '#7C8EA3',
    senary: '#A7B4C3',
  },
  semantic: {
    success: BRAND_COLORS.success,
    warning: BRAND_COLORS.warning,
    danger: BRAND_COLORS.error,
    info: BRAND_COLORS.info,
  },
  grid: BRAND_COLORS.slate100,
  axis: BRAND_COLORS.slate500,
} as const;

export type TypographyTokenName = keyof typeof TYPOGRAPHY_TOKENS;
